import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import Filtros from '../components/Filtros';
import TablaAvisos from '../components/TablaAvisos';
import { getAvisos } from '../services/apiService';
import { format, startOfMonth, endOfMonth } from 'date-fns';

const ReporteAvisos = () => {
  // Fecha inicial: primer día del mes actual
  const primerDiaMes = startOfMonth(new Date());
  // Fecha final: último día del mes actual
  const ultimoDiaMes = endOfMonth(new Date());

  const [avisos, setAvisos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filtrosActuales, setFiltrosActuales] = useState({
    fechaInicio: format(primerDiaMes, 'yyyy-MM-dd'),
    fechaFin: format(ultimoDiaMes, 'yyyy-MM-dd')
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState(null);
  const [error, setError] = useState(null);

  // Cargar avisos al inicio o cuando cambien los filtros
  useEffect(() => {
    const cargarAvisos = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log('Enviando filtros al servidor:', filtrosActuales);
        // Añadir parámetro de límite para asegurar que se muestren todos los avisos
        const filtrosConLimite = { ...filtrosActuales, limit: 100000 };
        const response = await getAvisos(filtrosConLimite);
        if (response.success) {
          setAvisos(response.data);
          setFiltrosAplicados(response.filtros);
        } else {
          setAvisos([]);
          setError('No se pudieron cargar los avisos. Por favor, intente de nuevo.');
        }
      } catch (error) {
        console.error('Error al cargar avisos:', error);
        setAvisos([]);
        setError('Error de conexión. Por favor, verifique su conexión e intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    };

    cargarAvisos();
  }, [filtrosActuales]);

  // Manejar cambios en los filtros
  const handleFilter = (filtros) => {
    console.log('Filtros recibidos del componente:', filtros);
    setFiltrosActuales(filtros);
  };
  
  // Manejadores para filtrar por ejecutivo y empresa cliente
  const handleFiltrarPorEjecutivo = (ejecutivo) => {
    console.log('Filtrando por ejecutivo:', ejecutivo);
    setFiltrosActuales({
      ...filtrosActuales,
      ejecutivo
    });
  };
  
  const handleFiltrarPorEmpresaCliente = (empresaCliente) => {
    console.log('Filtrando por empresa cliente:', empresaCliente);
    setFiltrosActuales({
      ...filtrosActuales,
      empresaCliente,
      empresaClienteNombre: empresaCliente // Si se requiere para la UI
    });
  };

  // Calcular totales para el resumen
  const calcularTotalesMonetarios = () => {
    if (!avisos.length) return { count: 0, total: 0 };
    
    const total = avisos.reduce(
      (sum, aviso) => sum + parseFloat(aviso.valor_con_descuento || 0), 
      0
    );
    
    return {
      count: avisos.length,
      total
    };
  };

  // Totales monetarios
  const totalesMonetarios = calcularTotalesMonetarios();
  
  // Calcular totales por estado
  const calcularTotalesPorEstado = () => {
    if (!avisos.length) return { publicados: 0, pendientes: 0 };
    
    return {
      publicados: avisos.filter(aviso => 
        aviso.estado_aviso_impreso === 'Publicado' || 
        aviso.estado_aviso_impreso === 'Estado 71'
      ).length,
      pendientes: avisos.filter(aviso => 
        aviso.estado_aviso_impreso === 'Pendiente' || 
        aviso.estado_aviso_impreso === 'Estado 68'
      ).length
    };
  };

  const totalesPorEstado = calcularTotalesPorEstado();

  // Calcular totales por estado para las tarjetas
  const totales = {
    total: avisos.length,
    publicados: avisos.filter(aviso => 
      aviso.estado_aviso_impreso === 'Publicado' || 
      aviso.estado_aviso_impreso === 'Estado 71'
    ).length,
    pendientes: avisos.filter(aviso => 
      aviso.estado_aviso_impreso === 'Pendiente' || 
      aviso.estado_aviso_impreso === 'Estado 68'
    ).length
  };

  // Formatear valor monetario
  const formatearMoneda = (valor) => {
    if (!valor) return '$0';
    return `$${parseInt(valor).toLocaleString('es-CL')}`;
  };

  // Renderizar información de filtros aplicados
  const renderFiltrosAplicados = () => {
    if (!filtrosAplicados) return null;
    
    return (
      <Alert variant="info" className="mb-3">
        <strong>Filtros aplicados:</strong> Desde {filtrosAplicados.fechaInicio} hasta {filtrosAplicados.fechaFin}
        {filtrosAplicados.ejecutivo && <span> | Ejecutivo: {filtrosAplicados.ejecutivo}</span>}
        {filtrosAplicados.medio && <span> | Medio: {filtrosAplicados.medio}</span>}
        {filtrosAplicados.estadoAviso && <span> | Estado: {filtrosAplicados.estadoAviso}</span>}
        {filtrosAplicados.empresaCliente && <span> | Empresa: {filtrosAplicados.empresaCliente}</span>}
      </Alert>
    );
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">Reporte de Avisos - Diario El Día</h1>
          <p className="text-muted">
            Consulta y filtra los avisos publicados en el sistema
          </p>
        </Col>
      </Row>

      <Filtros onFilter={handleFilter} fechaInicioDefault={primerDiaMes} fechaFinDefault={ultimoDiaMes} />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {renderFiltrosAplicados()}

      <Row className="mb-4">
        <Col md={6} lg={3}>
          <Card className="mb-3">
            <Card.Body>
              <h5 className="card-title">Total de Avisos</h5>
              <h2 className="card-text">{totales.total}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="mb-3 bg-primary text-white">
            <Card.Body>
              <h5 className="card-title">Valor Total</h5>
              <h2 className="card-text">{formatearMoneda(totalesMonetarios.total)}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="mb-3 bg-success text-white">
            <Card.Body>
              <h5 className="card-title">Publicados</h5>
              <h2 className="card-text">{totales.publicados}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="mb-3 bg-warning">
            <Card.Body>
              <h5 className="card-title">Pendientes</h5>
              <h2 className="card-text">{totales.pendientes}</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <TablaAvisos 
            avisos={avisos} 
            isLoading={isLoading} 
            onFiltrarPorEjecutivo={handleFiltrarPorEjecutivo}
            onFiltrarPorEmpresaCliente={handleFiltrarPorEmpresaCliente}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default ReporteAvisos; 